// ExistsEnrollmentModel.tsx
import React from "react";
import { enrollPng } from "../../../assets";

interface ExistsEnrollmentModelProps {
	onClose: () => void;
	onConfirm: () => void;
	isDeselecting?: boolean;
	dependentData?: any;
}

const ExistsEnrollmentModel: React.FC<ExistsEnrollmentModelProps> = ({
	onClose,
	onConfirm,
	isDeselecting = true,
	dependentData
}) => {
	return (
		<div
			style={{
				position: "fixed",
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				backgroundColor: "rgba(15, 23, 42, 0.6)",
				backdropFilter: "blur(8px)",
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				zIndex: 1000,
				animation: "fadeIn 0.3s ease-out",
				padding: "20px"
			}}
		>
			<style>{`
				@keyframes fadeIn {
					from { opacity: 0; }
					to { opacity: 1; }
				}
				@keyframes slideUp {
					from { opacity: 0; transform: translateY(20px) scale(0.95); }
					to { opacity: 1; transform: translateY(0) scale(1); }
				}
				.btn-3d {
					position: relative;
					transition: all 0.1s ease;
					box-shadow: 
						0 4px 0 0 #000,
						0 5px 10px rgba(0,0,0,0.3),
						inset 0 1px 0 rgba(255,255,255,0.1);
				}
				.btn-3d:active {
					transform: translateY(4px);
					box-shadow: 
						0 0 0 0 #000,
						0 1px 2px rgba(0,0,0,0.3),
						inset 0 2px 4px rgba(0,0,0,0.2);
				}
				.btn-3d-cancel {
					background: linear-gradient(145deg, #f3f4f6, #e5e7eb) !important;
					color: #374151 !important;
					box-shadow: 
						0 4px 0 0 #9ca3af,
						0 5px 10px rgba(0,0,0,0.2),
						inset 0 1px 0 rgba(255,255,255,0.8) !important;
				}
				.btn-3d-cancel:active {
					box-shadow: 
						0 0 0 0 #9ca3af,
						0 1px 2px rgba(0,0,0,0.2),
						inset 0 2px 4px rgba(0,0,0,0.1) !important;
				}
			`}</style>

			<div
				style={{
					background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
					padding: "0",
					borderRadius: "20px",
					boxShadow: `
						0 25px 50px -12px rgba(0, 0, 0, 0.25),
						0 0 0 1px rgba(255, 255, 255, 0.5) inset
					`,
					maxWidth: "480px",
					width: "100%",
					textAlign: "center",
					animation: "slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
					overflow: "hidden",
					position: "relative"
				}}
			>
				{/* Top accent bar */}
				<div
					style={{
						height: "4px",
						background: "linear-gradient(90deg, #f59e0b 0%, #d97706 50%, #f59e0b 100%)",
						width: "100%"
					}}
				/>

				<div style={{ padding: "40px 32px 32px" }}>
					{/* Icon Container */}
					<div
						style={{
							width: "90px",
							height: "90px",
							margin: "0 auto 24px",
							background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
							borderRadius: "24px",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							boxShadow: "0 10px 25px -5px rgba(245, 158, 11, 0.3)",
							position: "relative",
							overflow: "hidden"
						}}
					>
						<div
							style={{
								position: "absolute",
								top: "-50%",
								left: "-50%",
								width: "200%",
								height: "200%",
								background:
									"linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.3) 50%, transparent 70%)",
								animation: "shimmer 3s infinite"
							}}
						/>
						<style>{`
							@keyframes shimmer {
								0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
								100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
							}
						`}</style>
						<img
							src={enrollPng}
							alt="Duplicate SSN"
							style={{
								width: "48px",
								height: "48px",
								objectFit: "contain",
								filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
								position: "relative",
								zIndex: 1
							}}
						/>
					</div>

					{/* Title */}
					<h2
						style={{
							margin: "0 0 16px 0",
							fontSize: "1.875rem",
							color: "#0f172a",
							fontWeight: "700",
							letterSpacing: "-0.025em",
							lineHeight: 1.2
						}}
					>
						Attention Required
					</h2>

					{/* Message */}
					<div
						style={{
							marginBottom: "32px",
							padding: "20px",
							backgroundColor: "#fffbeb",
							borderRadius: "12px",
							border: "1px solid #fcd34d",
							position: "relative"
						}}
					>
						<div
							style={{
								position: "absolute",
								top: "-10px",
								left: "24px",
								backgroundColor: "#f59e0b",
								color: "white",
								padding: "4px 12px",
								borderRadius: "20px",
								fontSize: "0.75rem",
								fontWeight: "600",
								textTransform: "uppercase",
								letterSpacing: "0.05em"
							}}
						>
							Warning
						</div>
						<p
							style={{
								margin: "8px 0 0 0",
								fontSize: "1rem",
								color: "#92400e",
								lineHeight: "1.7",
								fontWeight: "500"
							}}
						>
							{isDeselecting ? (
								<>
									We are deselecting the dependent
									<span
										style={{
											background: "white",
											padding: "2px 8px",
											borderRadius: "6px",
											fontWeight: "700",
											color: "#78350f",
											boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
										}}
									>
										{dependentData?.first_name}
									</span>{" "}
									. The dependent is{" "}
									<span
										style={{
											background: "linear-gradient(120deg, #fcd34d 0%, #fbbf24 100%)",
											padding: "2px 8px",
											borderRadius: "6px",
											fontWeight: "700",
											color: "#78350f",
											boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
										}}
									>
										already enrolled
									</span>{" "}
									with another member. Please confirm to deselect.
								</>
							) : (
								<>
									The dependent you are trying to add to your{" "}
									<span
										style={{
											background: "linear-gradient(120deg, #fcd34d 0%, #fbbf24 100%)",
											padding: "2px 8px",
											borderRadius: "6px",
											fontWeight: "700",
											color: "#78350f",
											boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
										}}
									>
										benefit is already enrolled
									</span>{" "}
									with another member. Please review the details and revert the selection.
								</>
							)}
						</p>
					</div>

					{/* Buttons */}
					<div style={{ display: "flex", gap: "16px", justifyContent: "center" }}>
						<button
							className="btn-3d btn-3d-cancel"
							style={{
								flex: "1",
								backgroundColor: "#f3f4f6",
								color: "#374151",
								border: "none",
								padding: "14px 24px",
								borderRadius: "12px",
								cursor: "pointer",
								fontSize: "0.95rem",
								fontWeight: "600",
								maxWidth: "160px"
							}}
							onClick={onClose}
						>
							Cancel
						</button>
						<button
							className="btn-3d"
							style={{
								flex: "1",
								background: "linear-gradient(145deg, #1f2937 0%, #111827 100%)",
								color: "white",
								border: "none",
								padding: "14px 24px",
								borderRadius: "12px",
								cursor: "pointer",
								fontSize: "0.95rem",
								fontWeight: "600",
								maxWidth: "160px",
								textShadow: "0 1px 2px rgba(0,0,0,0.2)"
							}}
							onClick={onConfirm}
						>
							Agree
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ExistsEnrollmentModel;
